import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save, Printer, Camera } from "lucide-react";
import { toast } from "sonner";
import { ClientPicker } from "@/components/ClientPicker";

export const Route = createFileRoute("/tools/site-surveys")({
  head: () => ({ meta: [{ title: "Site Surveys" }, { name: "robots", content: "noindex" }] }),
  component: SurveyPage,
});

type Room = { id: string; name: string; dimensions: string; existing: string; planned: string; notes: string };
type Photo = { url: string; caption: string };
type S = {
  id?: string; client_id?: string | null; client_name: string; client_address: string | null; client_phone: string | null; client_email: string | null;
  surveyed_at: string | null; surveyor: string | null;
  network_notes: string | null; power_notes: string | null; general_notes: string | null;
  rooms: Room[]; photos: Photo[];
};

const empty = (): S => ({ client_name: "", client_address: "", client_phone: "", client_email: "", surveyed_at: new Date().toISOString().slice(0, 10), surveyor: "", network_notes: "", power_notes: "", general_notes: "", rooms: [], photos: [] });

function SurveyPage() {
  const [list, setList] = useState<S[]>([]);
  const [current, setCurrent] = useState<S | null>(null);

  const load = async () => {
    const { data } = await supabase.from("site_surveys").select("*").order("created_at", { ascending: false });
    setList((data as any) || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!current || !current.client_name) return toast.error("Client name required");
    if (current.id) {
      const { error } = await supabase.from("site_surveys").update(current).eq("id", current.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("site_surveys").insert(current as any);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved"); setCurrent(null); load();
  };
  const del = async (id: string) => { if (confirm("Delete?")) { await supabase.from("site_surveys").delete().eq("id", id); load(); } };

  const addRoom = () => setCurrent(c => c && ({ ...c, rooms: [...c.rooms, { id: crypto.randomUUID(), name: "", dimensions: "", existing: "", planned: "", notes: "" }] }));
  const updateRoom = (i: number, patch: Partial<Room>) => setCurrent(c => c && ({ ...c, rooms: c.rooms.map((r, idx) => idx === i ? { ...r, ...patch } : r) }));
  const removeRoom = (i: number) => setCurrent(c => c && ({ ...c, rooms: c.rooms.filter((_, idx) => idx !== i) }));

  const onPhoto = async (files: FileList | null) => {
    if (!files || !current) return;
    const newPhotos: Photo[] = [];
    for (const file of Array.from(files)) {
      const path = `surveys/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error } = await supabase.storage.from("job-photos").upload(path, file);
      if (error) { toast.error(error.message); continue; }
      const { data } = await supabase.storage.from("job-photos").createSignedUrl(path, 60 * 60 * 24 * 365);
      if (data?.signedUrl) newPhotos.push({ url: data.signedUrl, caption: file.name });
    }
    setCurrent({ ...current, photos: [...current.photos, ...newPhotos] });
  };

  if (!current) return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold">Site Surveys</h1>
        <Button onClick={() => setCurrent(empty())}><Plus className="h-4 w-4 mr-1" />New Survey</Button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map(s => (
          <div key={s.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{s.client_name}</h3>
                <p className="text-sm text-muted-foreground">{s.client_address || "—"}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.surveyed_at || "—"} · {s.rooms.length} rooms · {s.photos.length} photos</p>
              </div>
              <button onClick={() => del(s.id!)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
            <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setCurrent(s)}>Open</Button>
          </div>
        ))}
        {list.length === 0 && <p className="text-muted-foreground text-sm">No surveys yet.</p>}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between print:hidden">
        <Button variant="ghost" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()}><Printer className="h-4 w-4 mr-1" />Print</Button>
          <Button onClick={save}><Save className="h-4 w-4 mr-1" />Save</Button>
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2"><ClientPicker clientId={current.client_id} clientName={current.client_name} onPick={(f) => setCurrent({ ...current, client_id: f.client_id, client_name: f.client_name ?? "", client_email: f.client_email, client_phone: f.client_phone, client_address: f.client_address })} /></div>
        <div><label className="text-xs text-muted-foreground">Client Name</label><Input value={current.client_name} onChange={e => setCurrent({ ...current, client_name: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Address</label><Input value={current.client_address || ""} onChange={e => setCurrent({ ...current, client_address: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Phone</label><Input value={current.client_phone || ""} onChange={e => setCurrent({ ...current, client_phone: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Email</label><Input value={current.client_email || ""} onChange={e => setCurrent({ ...current, client_email: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Date Surveyed</label><Input type="date" value={current.surveyed_at || ""} onChange={e => setCurrent({ ...current, surveyed_at: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Surveyor</label><Input value={current.surveyor || ""} onChange={e => setCurrent({ ...current, surveyor: e.target.value })} /></div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <h3 className="font-semibold">Rooms</h3>
        <Button size="sm" onClick={addRoom}><Plus className="h-4 w-4 mr-1" />Add Room</Button>
      </div>
      <div className="mt-2 space-y-3">
        {current.rooms.map((r, i) => (
          <div key={r.id} className="rounded-lg border p-3 bg-card">
            <div className="grid gap-2 sm:grid-cols-2">
              <Input placeholder="Room name (e.g. Great Room)" value={r.name} onChange={e => updateRoom(i, { name: e.target.value })} />
              <Input placeholder="Dimensions (e.g. 20x18x10)" value={r.dimensions} onChange={e => updateRoom(i, { dimensions: e.target.value })} />
              <Textarea placeholder="Existing equipment" value={r.existing} onChange={e => updateRoom(i, { existing: e.target.value })} />
              <Textarea placeholder="Planned equipment" value={r.planned} onChange={e => updateRoom(i, { planned: e.target.value })} />
              <Textarea placeholder="Notes / obstacles" value={r.notes} onChange={e => updateRoom(i, { notes: e.target.value })} className="sm:col-span-2" />
            </div>
            <div className="mt-2 text-right"><button onClick={() => removeRoom(i)} className="text-destructive text-sm inline-flex items-center gap-1"><Trash2 className="h-3 w-3" />Remove Room</button></div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <div><label className="text-xs text-muted-foreground">Network / ISP notes</label><Textarea value={current.network_notes || ""} onChange={e => setCurrent({ ...current, network_notes: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Power / electrical notes</label><Textarea value={current.power_notes || ""} onChange={e => setCurrent({ ...current, power_notes: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">General notes</label><Textarea value={current.general_notes || ""} onChange={e => setCurrent({ ...current, general_notes: e.target.value })} /></div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Photos</h3>
          <label className="inline-flex items-center gap-1 rounded border border-input px-3 py-1.5 text-sm cursor-pointer hover:bg-accent"><Camera className="h-4 w-4" />Add<input type="file" accept="image/*" multiple className="hidden" onChange={e => onPhoto(e.target.files)} /></label>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {current.photos.map((p, i) => (
            <div key={i} className="relative">
              <img src={p.url} alt={p.caption} className="w-full h-32 object-cover rounded" />
              <button onClick={() => setCurrent({ ...current, photos: current.photos.filter((_, idx) => idx !== i) })} className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 print:hidden"><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
